/**
 * THE OAUTH RETURN HINT (FE2 plan 4.2; Google contract revision 6, "context
 * and resume").
 *
 * Before `location.assign(start_url)` the provider writes where the learner
 * was; after the callback lands on `/work` or `/squad`, the return route finds
 * it and hands its KEY to Dashboard in router state. The provider restores it
 * only after a fresh authorized bootstrap for the SAME user, squad and capsule.
 *
 *   sessionStorage   per tab, so two tabs can never restore each other's hint
 *   key              `ws.google.return:{userId}:{squadId}:{capsuleId}`
 *   TTL              10 minutes; expired and malformed values are deleted
 *   fields           the allowlist below, nothing else. No token, Picker
 *                    nonce, URL, document body, consent, executable action or
 *                    pending mutation intent is ever written.
 *   resume           (FE4 D-3, contract amendment to :262) one optional,
 *                    NON-EXECUTABLE marker. Its only accepted value is
 *                    `create_document`; anything else is dropped here. It is
 *                    written only when a connect was started from a Create
 *                    press on a task, and it only lets the restored task show
 *                    an explicit Continue creating. Nothing reads it except
 *                    the restore, and nothing is ever created from it.
 *
 * Every read and write is wrapped: storage can be unavailable (private mode,
 * blocked site data), and then the learner simply lands in the default view.
 */

export const HINT_PREFIX = "ws.google.return:";
export const HINT_TTL_MS = 10 * 60 * 1000;

const VIEWS = ["work", "squad"];
const ID_FIELDS = ["task_id", "document_id", "reference_id"];
const NUMBER_FIELDS = ["scroll_top", "video_position_seconds"];
export const RESUME_CREATE = "create_document";

export const returnHintKey = (userId, squadId, capsuleId) =>
  `${HINT_PREFIX}${Number(userId)}:${Number(squadId)}:${Number(capsuleId)}`;

const positiveInt = (value) => {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
};

/** The allowlisted hint, or null when a required field is invalid. */
const sanitize = (raw, savedAt) => {
  if (!raw || typeof raw !== "object") return null;
  const userId = positiveInt(raw.user_id);
  const squadId = positiveInt(raw.squad_id);
  const capsuleId = positiveInt(raw.capsule_id);
  if (!userId || !squadId || !capsuleId || !VIEWS.includes(raw.view)) {
    return null;
  }
  const hint = {
    user_id: userId,
    squad_id: squadId,
    capsule_id: capsuleId,
    view: raw.view,
  };
  ID_FIELDS.forEach((field) => {
    const id = positiveInt(raw[field]);
    if (id) hint[field] = id;
  });
  NUMBER_FIELDS.forEach((field) => {
    const number = Number(raw[field]);
    if (raw[field] !== undefined && Number.isFinite(number) && number >= 0) {
      hint[field] = Math.round(number);
    }
  });
  /* The resume marker is meaningful only with the task it belongs to. */
  if (raw.resume === RESUME_CREATE && hint.task_id) {
    hint.resume = RESUME_CREATE;
  }
  const saved = Number(savedAt);
  if (!Number.isFinite(saved)) return null;
  hint.saved_at = saved;
  return hint;
};

const storage = () => {
  try {
    return window.sessionStorage ?? null;
  } catch (error) {
    return null;
  }
};

const remove = (key) => {
  try {
    storage()?.removeItem(key);
  } catch (error) {
    /* Unavailable storage has nothing to remove. */
  }
};

/** Writes the hint; answers its key, or null when nothing was written. */
export const writeReturnHint = (raw, now = Date.now()) => {
  const hint = sanitize(raw, now);
  if (!hint) return null;
  const key = returnHintKey(hint.user_id, hint.squad_id, hint.capsule_id);
  try {
    storage()?.setItem(key, JSON.stringify(hint));
    return key;
  } catch (error) {
    return null;
  }
};

/**
 * The stored hint under `key` if it is well formed, matches its own key and
 * is not expired; otherwise deletes it and answers null.
 */
export const readReturnHint = (key, now = Date.now()) => {
  if (typeof key !== "string" || !key.startsWith(HINT_PREFIX)) return null;
  let raw = null;
  try {
    raw = storage()?.getItem(key) ?? null;
  } catch (error) {
    return null;
  }
  if (raw === null) return null;
  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    remove(key);
    return null;
  }
  const hint = sanitize(parsed, parsed?.saved_at);
  if (
    !hint ||
    returnHintKey(hint.user_id, hint.squad_id, hint.capsule_id) !== key ||
    now - hint.saved_at > HINT_TTL_MS ||
    hint.saved_at - now > HINT_TTL_MS
  ) {
    remove(key);
    return null;
  }
  return hint;
};

export const deleteReturnHint = (key) => {
  if (typeof key === "string" && key.startsWith(HINT_PREFIX)) remove(key);
};

const hintKeys = () => {
  const store = storage();
  if (!store) return [];
  const keys = [];
  try {
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key && key.startsWith(HINT_PREFIX)) keys.push(key);
    }
  } catch (error) {
    return [];
  }
  return keys;
};

/**
 * The key of this user's newest valid hint, or null. Expired and malformed
 * hints met on the way are deleted. Another user's hints are left alone.
 */
export const findReturnHintKey = (userId, now = Date.now()) => {
  const id = positiveInt(userId);
  if (!id) return null;
  const mine = `${HINT_PREFIX}${id}:`;
  let best = null;
  hintKeys()
    .filter((key) => key.startsWith(mine))
    .forEach((key) => {
      const hint = readReturnHint(key, now);
      if (hint && (!best || hint.saved_at > best.savedAt)) {
        best = { key, savedAt: hint.saved_at };
      }
    });
  return best?.key ?? null;
};

/** Logout: every return hint in this tab goes. */
export const clearAllReturnHints = () => {
  hintKeys().forEach(remove);
};
